/**
 * User permission helpers — capability-based checks.
 *
 * Use these instead of comparing `user.user_type` directly. user_type is
 * deprecated and will be removed once all callsites migrate.
 *
 * Conventions:
 *   - `is*()` checks position (EMPLOYEE / MANAGER / DIRECTOR / DG)
 *   - `can*()` checks specific capabilities via permissions[]
 */
import { User, PositionType } from '../types';

// ─── Position checks ─────────────────────────────────────────────────────

export const isEmployee = (user: User | null | undefined): boolean =>
  user?.position_type === 'EMPLOYEE';

export const isSectionHead = (user: User | null | undefined): boolean =>
  user?.position_type === 'MANAGER';

export const isDirector = (user: User | null | undefined): boolean =>
  user?.position_type === 'DIRECTOR';

export const isDG = (user: User | null | undefined): boolean =>
  user?.position_type === 'DG';

/** MANAGER OR DIRECTOR — replaces legacy user_type === 'org_executive' */
export const isOrgExecutive = (user: User | null | undefined): boolean =>
  isSectionHead(user) || isDirector(user);

// ─── Capability checks (permission-based) ────────────────────────────────

export const hasPerm = (user: User | null | undefined, perm: string): boolean => {
  if (!user) return false;
  if (user.is_superuser) return true;
  return user.permissions?.includes(perm) ?? false;
};

/** True if user can create strategic goals (= "Strategy Team" capability).
 *  This replaces the legacy concept of a "Strategy Team role". */
export const canCreateStrategicGoals = (user: User | null | undefined): boolean =>
  hasPerm(user, 'hrcpms.strategic_goals.create');

export const canApproveExecutiveItems = (user: User | null | undefined): boolean =>
  hasPerm(user, 'hrcpms.executive_items.approve');

export const canApproveReject = (user: User | null | undefined): boolean =>
  hasPerm(user, 'hrcpms.review.approve_reject');

/** Replaces legacy user_type === 'performance_evaluator'. */
export const isPerformanceEvaluator = (user: User | null | undefined): boolean =>
  hasPerm(user, 'sms.performance_evaluator');

/** Replaces legacy user_type === 'evaluation_approver'. */
export const isEvaluationApprover = (user: User | null | undefined): boolean =>
  hasPerm(user, 'sms.evaluation_approver');

/** True if user is on the evaluation team (either role). Used for
 *  routing decisions (lands on EvaluationStack) and the cross-org
 *  visibility flag. */
export const isEvaluator = (user: User | null | undefined): boolean =>
  isPerformanceEvaluator(user) || isEvaluationApprover(user);

/** True for superuser, Director (review.approve_reject), Section Head (managing
 *  own dept), Strategy Team (global). Used for delete-approval gating. */
export const canApproveDeletion = (user: User | null | undefined): boolean => {
  if (!user) return false;
  if (user.is_superuser) return true;
  return isOrgExecutive(user) || canCreateStrategicGoals(user);
};

/** Cross-department visibility — superuser, Strategy Team, DG, evaluators. */
export const canViewAllDepartments = (user: User | null | undefined): boolean => {
  if (!user) return false;
  if (user.is_superuser) return true;
  return canCreateStrategicGoals(user) || isDG(user) || isEvaluator(user);
};

// ─── Display label ───────────────────────────────────────────────────────

const POSITION_LABEL_AR: Record<PositionType, string> = {
  EMPLOYEE: 'موظف',
  MANAGER: 'رئيس قسم',
  DIRECTOR: 'مدير إدارة',
  DG: 'مدير الدائرة',
};

export const getPositionLabel = (user: User | null | undefined): string => {
  if (!user?.position_type) return '';
  return POSITION_LABEL_AR[user.position_type] || '';
};
